<template>
  <nav
    ref="sideNavRef"
    class="side-bar"
    :class="{ 'temp-open': tempOpen, collapsed: collapsed && !tempOpen }"
    @mouseenter="!isMobile && debouncedupdateTempOpen(true)"
    @mouseleave="!isMobile && debouncedupdateTempOpen(false)"
  >
    <!-- Branding -->
    <div class="sb-brand">
      <NuxtLink
        to="/landing-page"
        class="sb-logo"
      >
        <Icon
          :data="appLogoCropped"
          original
          height="32"
          width="auto"
        />
      </NuxtLink>
      <button
        class="sb-collapse-btn"
        :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="collapsed = !collapsed"
      >
        <v-icon size="15">{{
          collapsed ? "mdi-chevron-right" : "mdi-chevron-left"
        }}</v-icon>
      </button>
    </div>

    <!-- Nav -->
    <div class="sb-nav">
      <!-- Dashboard -->
      <NuxtLink
        to="/"
        class="sb-item"
        data-tour="dashboard"
        :class="{ active: routeName === 'index' }"
      >
        <v-icon size="20">mdi-view-dashboard-outline</v-icon>
        <span class="sb-label">Dashboard</span>
      </NuxtLink>

      <!-- ── Internal Ticketing ── -->
      <div
        class="sb-section"
        data-tour="internal"
      >
        <button
          class="sb-section-header"
          :class="{ active: isInternalActive }"
          @click="toggleSection('internal')"
        >
          <v-icon size="20">mdi-ticket-outline</v-icon>
          <span class="sb-label">Internal Ticketing</span>
          <v-icon
            size="14"
            class="sb-chevron"
            :class="{ open: sections.internal }"
            >mdi-chevron-down</v-icon
          >
        </button>
        <Transition name="sb-expand">
          <div
            v-if="sections.internal"
            class="sb-children"
          >
            <NuxtLink
              to="/tickets"
              class="sb-child"
              :class="{
                active:
                  routeName === 'tickets' ||
                  String(routeName).startsWith('tickets'),
              }"
            >
              <v-icon size="15">mdi-format-list-bulleted</v-icon>
              <span class="sb-label">Tickets</span>
            </NuxtLink>

            <!-- <NuxtLink
              to="/calendar"
              class="sb-child"
              :class="{ active: routeName === 'calendar' }"
            >
              <v-icon size="15">mdi-calendar-month-outline</v-icon>
              <span class="sb-label">Calendar</span>
            </NuxtLink> -->
            <NuxtLink
              v-if="isSuperAdmin"
              to="/audit"
              class="sb-child"
              :class="{ active: routeName === 'audit' }"
            >
              <v-icon size="15">mdi-clipboard-text-clock-outline</v-icon>
              <span class="sb-label">Audit Log</span>
            </NuxtLink>
            <!-- Ticket Settings sub-group -->
            <div class="sb-subgroup">
              <button
                class="sb-subgroup-header"
                :class="{ active: isTicketSettingsActive }"
                @click="toggleSection('ticketSettings')"
              >
                <v-icon size="14">mdi-cog-outline</v-icon>
                <span class="sb-label">Ticket Settings</span>
                <v-icon
                  size="12"
                  class="sb-chevron"
                  :class="{ open: sections.ticketSettings }"
                  >mdi-chevron-down</v-icon
                >
              </button>
              <Transition name="sb-expand">
                <div
                  v-if="sections.ticketSettings"
                  class="sb-subchildren"
                >
                  <NuxtLink
                    to="/admin/ticket/templates"
                    class="sb-subchild"
                    :class="{
                      active: routeName?.startsWith('admin-ticket'),
                      'sb-child--locked': !can('ticket_types'),
                    }"
                  >
                    <span class="sb-label">Ticket Types</span>
                    <v-icon
                      v-if="!can('ticket_types')"
                      size="11"
                      class="sb-lock-icon"
                      >mdi-lock-outline</v-icon
                    >
                  </NuxtLink>
                  <NuxtLink
                    to="/admin/workflows"
                    class="sb-subchild"
                    :class="{
                      active:
                        routeName === 'admin-ticket-workflows' ||
                        routeName?.startsWith('admin-workflows'),
                      'sb-child--locked': !can('workflow_builder'),
                    }"
                  >
                    <span class="sb-label">Ticket Workflow Builder</span>
                    <v-icon
                      v-if="!can('workflow_builder')"
                      size="11"
                      class="sb-lock-icon"
                      >mdi-lock-outline</v-icon
                    >
                  </NuxtLink>
                </div>
              </Transition>
            </div>
          </div>
        </Transition>
      </div>

      <!-- ── Admin ── -->
      <div class="sb-section">
        <button
          class="sb-section-header"
          :class="{ active: isAdminActive }"
          @click="toggleSection('admin')"
        >
          <v-icon size="20">mdi-cog-outline</v-icon>
          <span class="sb-label">Admin Settings</span>
          <v-icon
            size="14"
            class="sb-chevron"
            :class="{ open: sections.admin }"
            >mdi-chevron-down</v-icon
          >
        </button>
        <Transition name="sb-expand">
          <div
            v-if="sections.admin"
            class="sb-children"
          >
            <NuxtLink
              to="/admin/user-management"
              class="sb-child"
              :class="{ active: routeName === 'admin-user-management' }"
            >
              <v-icon size="15">mdi-account-group-outline</v-icon>
              <span class="sb-label">People Management</span>
            </NuxtLink>
            <NuxtLink
              to="/admin/departments"
              class="sb-child"
              :class="{ active: routeName === 'admin-departments' }"
            >
              <v-icon size="15">mdi-domain</v-icon>
              <span class="sb-label">Departments</span>
            </NuxtLink>
            <NuxtLink
              to="/admin/roles"
              class="sb-child"
              :class="{ active: routeName === 'admin-roles' }"
            >
              <v-icon size="15">mdi-shield-account-outline</v-icon>
              <span class="sb-label">Roles & Permissions</span>
            </NuxtLink>
            <!-- <NuxtLink
              to="/admin/support-settings"
              class="sb-child"
              :class="{
                active: routeName?.startsWith('admin-support-settings'),
              }"
            >
              <v-icon size="15">mdi-headset</v-icon>
              <span class="sb-label">Support Settings</span>
            </NuxtLink> -->
          </div>
        </Transition>
      </div>

      <!-- ── Compliance ── -->
      <div class="sb-section">
        <button
          class="sb-section-header"
          :class="{
            active: isComplianceActive,
            'sb-item--locked': !can('compliance'),
          }"
          @click="toggleSection('compliance')"
        >
          <v-icon size="20">mdi-shield-half-full</v-icon>
          <span class="sb-label">Compliance</span>
          <v-icon
            v-if="!can('compliance')"
            size="12"
            class="sb-lock-icon"
            >mdi-lock-outline</v-icon
          >
          <v-icon
            v-else
            size="14"
            class="sb-chevron"
            :class="{ open: sections.compliance }"
            >mdi-chevron-down</v-icon
          >
        </button>
        <Transition name="sb-expand">
          <div
            v-if="sections.compliance"
            class="sb-children"
          >
            <NuxtLink
              to="/admin/policies"
              class="sb-child"
              :class="{ active: routeName === 'admin-policies' }"
            >
              <v-icon size="15">mdi-file-document-outline</v-icon>
              <span class="sb-label">Policies</span>
            </NuxtLink>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Bottom actions -->
    <div class="sb-bottom">
      <NuxtLink
        :to="{ name: 'tickets', query: { action: 'create' } }"
        class="sb-new-ticket"
      >
        <v-icon size="18">mdi-plus</v-icon>
        <span class="sb-label">New Ticket</span>
      </NuxtLink>
      <NuxtLink
        to="/profile?tab=subscription"
        class="sb-subscription"
        :class="{
          active: routeName === 'profile' && route.query.tab === 'subscription',
        }"
      >
        <v-icon size="18">mdi-credit-card-outline</v-icon>
        <span class="sb-label">Subscription</span>
      </NuxtLink>
      <!-- Plan badge + tour trigger -->
      <div class="sb-plan-row">
        <NuxtLink
          to="/profile?tab=subscription"
          class="sb-plan-badge"
          :title="planMeta.label + ' plan'"
        >
          <span
            class="sb-plan-badge__dot"
            :style="{ background: planMeta.color }"
          />
          <span class="sb-label sb-plan-badge__label">{{
            planMeta.label
          }}</span>
        </NuxtLink>
        <button
          class="sb-tour-btn"
          title="Take the tour"
          @click="() => setShowPlatformTour?.(true)"
        >
          <v-icon size="18">mdi-help-circle-outline</v-icon>
        </button>
      </div>
      <button
        class="sb-logout"
        @click="emits('logout')"
      >
        <v-icon size="18">mdi-logout-variant</v-icon>
        <span class="sb-label">Logout</span>
      </button>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { useDisplay } from "vuetify";
import { appLogoCropped } from "@/composables/app";
import { useSidebarStore } from "~/stores/sidebar";
import { useSubscriptionPlanStore } from "~/stores/subscriptionPlan";
import { PlatformTourProviderDisplayContext } from "~/types/symbols";

interface Emits {
  (e: "logout"): void;
}
const emits = defineEmits<Emits>();
const { collapsed, tempOpen } = useSidebarStore();
const debouncedupdateTempOpen = useDebounceFn((bool: boolean) => {
  tempOpen.value = bool;
}, 1000);
const sideNavRef = ref<HTMLElement | null>(null);

const session = useSessionStore();
const { mobile: isMobile } = useDisplay();
const route = useRoute();
const { setShow: setShowPlatformTour } = inject(
  PlatformTourProviderDisplayContext,
  {},
);

onClickOutside(sideNavRef, () => (tempOpen.value = false));

const routeName = computed(() => String(route.name));
const planStore = useSubscriptionPlanStore();
const can = (f: Parameters<typeof planStore.hasFeature>[0]) =>
  planStore.hasFeature(f);
const planMeta = computed(() => planStore.meta);

const isSuperAdmin = computed(
  () =>
    session.userData?.role?.defaultRole === "super_admin" ||
    session.userData?.companyUser?.isAdmin ||
    session.userData?.company?.isAdmin ||
    false,
);

const isKbActive = computed(() => routeName.value.startsWith("knowledge-base"));

const isTicketSettingsActive = computed(() =>
  [
    "admin-ticket",
    "admin-ticket-workflows",
    "admin-workflows",
    "admin-releases",
  ].some((n) => routeName.value === n || routeName.value.startsWith(n)),
);

const isComplianceActive = computed(() =>
  ["admin-governance", "admin-security", "admin-policies"].includes(
    routeName.value,
  ),
);

const isSprintActive = computed(() =>
  String(routeName.value).startsWith("sprint"),
);

const isInternalActive = computed(
  () =>
    [
      "tickets",
      "tickets-index",
      "tickets-id",
      "calendar",
      "analytics",
      "audit",
    ].some((n) => routeName.value === n || routeName.value.startsWith(n)) ||
    isTicketSettingsActive.value ||
    isSprintActive.value,
);

const isSupportActive = computed(() => routeName.value.startsWith("support"));

const isAdminActive = computed(
  () =>
    routeName.value.startsWith("admin") &&
    !isTicketSettingsActive.value &&
    !isComplianceActive.value &&
    routeName.value !== "admin-developer",
);

const sections = reactive({
  kb: false,
  internal: true,
  ticketSettings: false,
  sprint: false,
  support: false,
  admin: false,
  compliance: false,
});

watchEffect(() => {
  if (isKbActive.value) sections.kb = true;
  if (isInternalActive.value) sections.internal = true;
  if (isTicketSettingsActive.value) {
    sections.internal = true;
    sections.ticketSettings = true;
  }
  if (isSprintActive.value) {
    sections.internal = true;
    sections.sprint = true;
  }
  if (isSupportActive.value) sections.support = true;
  if (isAdminActive.value) sections.admin = true;
  if (isComplianceActive.value) sections.compliance = true;
});

function toggleSection(key: keyof typeof sections) {
  sections[key] = !sections[key];
}
</script>

<style scoped lang="scss">
.side-bar {
  position: fixed;
  top: 12px;
  left: 12px;
  bottom: 12px;
  z-index: 100;
  width: 224px;
  background: var(--app-white);
  border-radius: 20px;
  box-shadow: 0 4px 24px var(--app-slate-900-alpha-8);
  display: none;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    display: flex;
  }

  &.temp-open {
    display: flex;
  }

  &.collapsed {
    width: 72px;

    .sb-label,
    .sb-chevron {
      display: none;
    }

    .sb-brand {
      padding: 16px 0;
      justify-content: center;
    }
    .sb-collapse-btn {
      display: flex;
    }

    .sb-section-header,
    .sb-item,
    .sb-child,
    .sb-new-ticket,
    .sb-subscription,
    .sb-logout {
      justify-content: center;
      padding: 10px 0;
    }

    .sb-children {
      display: none;
    }

    .sb-plan-row {
      justify-content: center;
      padding: 2px 0;
    }

    .sb-plan-badge {
      display: none;
    }
  }
}

// ── Brand ──────────────────────────────────────────────────────
.sb-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px 12px;
  flex-shrink: 0;
}

.sb-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;
}

.sb-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: var(--app-slate-400);
  transition:
    background 0.12s,
    color 0.12s;
  flex-shrink: 0;

  &:hover {
    background: var(--app-slate-100);
    color: var(--app-slate-700);
  }
}

// ── Nav container ───────────────────────────────────────────────
.sb-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--app-slate-200);
    border-radius: 3px;
  }
}

// ── Standalone item ────────────────────────────────────────────
.sb-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--app-slate-500);
  text-decoration: none;
  transition:
    background 0.12s,
    color 0.12s;
  white-space: nowrap;

  &:hover {
    background: var(--app-slate-50);
    color: var(--app-color-foreground);
  }

  &.active {
    background: rgba(var(--md-primary-rgb, var(--app-primary-rgb)), 0.1);
    color: var(--md-primary, var(--app-primary-500));
    font-weight: 600;
  }
}

// ── Section (collapsible group) ────────────────────────────────
.sb-section {
  display: flex;
  flex-direction: column;
}

.sb-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--app-slate-500);
  width: 100%;
  text-align: left;
  white-space: nowrap;
  transition:
    background 0.12s,
    color 0.12s;

  .sb-chevron {
    margin-left: auto;
    transition: transform 0.2s;
    &.open {
      transform: rotate(180deg);
    }
  }

  &:hover {
    background: var(--app-slate-50);
    color: var(--app-color-foreground);
  }

  &.active {
    color: var(--md-primary, var(--app-primary-500));
    font-weight: 600;
  }
}

.sb-children {
  padding-left: 14px;
  padding-bottom: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  border-left: 2px solid var(--app-slate-100);
  margin-left: 20px;
  margin-top: 2px;
}

.sb-child {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--app-slate-500);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 0.12s,
    color 0.12s;

  &:hover {
    background: var(--app-slate-50);
    color: var(--app-color-foreground);
  }

  &.active {
    background: rgba(var(--md-primary-rgb, var(--app-primary-rgb)), 0.1);
    color: var(--md-primary, var(--app-primary-500));
    font-weight: 600;
  }
}

// ── Sub-group (nested collapsible within children) ─────────────
.sb-subgroup {
  display: flex;
  flex-direction: column;
  margin-top: 2px;
}

.sb-subgroup-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--app-slate-500);
  width: 100%;
  text-align: left;
  white-space: nowrap;
  transition:
    background 0.12s,
    color 0.12s;

  .sb-chevron {
    margin-left: auto;
    transition: transform 0.2s;
    &.open {
      transform: rotate(180deg);
    }
  }

  &:hover {
    background: var(--app-slate-50);
    color: var(--app-slate-600);
  }

  &.active {
    color: var(--app-blue-502);
  }
}

.sb-subchildren {
  padding-left: 10px;
  padding-bottom: 2px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  border-left: 2px solid var(--app-slate-200);
  margin-left: 16px;
  margin-top: 1px;
}

.sb-subchild {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--app-slate-500);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 0.12s,
    color 0.12s;

  &:hover {
    background: var(--app-slate-50);
    color: var(--app-color-foreground);
  }

  &.active {
    background: rgba(var(--md-primary-rgb, var(--app-primary-rgb)), 0.1);
    color: var(--md-primary, var(--app-primary-500));
    font-weight: 600;
  }
}

// ── Expand transition ──────────────────────────────────────────
.sb-expand-enter-active,
.sb-expand-leave-active {
  transition:
    max-height 0.28s ease,
    opacity 0.2s ease;
  overflow: hidden;
  max-height: 1200px;
}
.sb-expand-enter-from,
.sb-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

// ── Locked state ───────────────────────────────────────────────
.sb-lock-icon {
  margin-left: auto;
  color: var(--app-slate-400) !important;
  flex-shrink: 0;
}

.sb-item--locked,
.sb-child--locked {
  opacity: 0.55;

  &:hover {
    background: var(--app-amber-53) !important;
    color: var(--app-amber-800) !important;
  }
}

.sb-inbox-badge {
  margin-left: auto;
  background: var(--app-indigo-500);
  color: var(--app-white);
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 0.62rem;
  font-weight: 700;
  flex-shrink: 0;
}

// ── Bottom ─────────────────────────────────────────────────────
.sb-bottom {
  flex-shrink: 0;
  padding: 10px 10px 14px;
  border-top: 1px solid var(--app-slate-100);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sb-new-ticket {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 600;
  background: var(--md-primary, var(--app-primary-500));
  color: var(--app-white);
  text-decoration: none;
  white-space: nowrap;
  transition: filter 0.12s;

  &:hover {
    filter: brightness(1.08);
  }
}

.sb-subscription {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--app-slate-500);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 0.12s,
    color 0.12s;

  &:hover {
    background: var(--app-slate-50);
    color: var(--app-color-foreground);
  }

  &.active {
    background: rgba(var(--md-primary-rgb, var(--app-primary-rgb)), 0.1);
    color: var(--md-primary, var(--app-primary-500));
    font-weight: 600;
  }
}

.sb-logout {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--app-slate-400);
  width: 100%;
  text-align: left;
  white-space: nowrap;
  transition:
    background 0.12s,
    color 0.12s;

  &:hover {
    background: var(--app-red-50);
    color: var(--app-red-500);
  }
}

// ── Plan row (badge + tour button) ─────────────────────────────
.sb-plan-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px 2px 8px;
}

.sb-plan-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--app-slate-50);
  border: 1px solid var(--app-slate-200);
  text-decoration: none;
  transition:
    background 0.12s,
    border-color 0.12s;
  flex: 1;
  min-width: 0;

  &:hover {
    background: var(--app-slate-100);
    border-color: var(--app-slate-300);
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__label {
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    color: var(--app-slate-600);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.sb-tour-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--app-slate-50);
  border: 1px solid var(--app-slate-200);
  color: var(--app-slate-400);
  transition: all 0.15s;

  &:hover {
    background: var(--md-primary, var(--app-primary-500));
    border-color: var(--md-primary, var(--app-primary-500));
    color: var(--app-white);
  }
}
</style>
